import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar as CalendarIcon, Clock, Filter, X } from 'lucide-react';
import { DevOpsTask } from '../../types/devopsTasks';
import { User as UserType } from '../../types';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

const locales = {
  'en-US': enUS
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarViewProps {
  currentUser: UserType;
  onTaskClick: (taskId: string) => void;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: DevOpsTask;
}

const priorityColors = {
  low: { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
  medium: { bg: '#fef3c7', border: '#eab308', text: '#854d0e' },
  high: { bg: '#fed7aa', border: '#f97316', text: '#9a3412' },
  critical: { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' }
};

const CalendarView: React.FC<CalendarViewProps> = ({ currentUser, onTaskClick }) => {
  const [tasks, setTasks] = useState<DevOpsTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignee: ''
  });

  useEffect(() => {
    fetchTasks();
  }, [currentUser]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        userId: currentUser.id,
        userRole: currentUser.role
      });

      const response = await fetch(`/api/tasks?${queryParams}`);
      if (!response.ok) {
        toast(await readApiError(response, 'Failed to load tasks'), 'error');
        return;
      }
      const data = await response.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const events: CalendarEvent[] = useMemo(() => {
    return tasks
      .filter(task => {
        // Apply filters
        if (filters.status && task.status !== filters.status) return false;
        if (filters.priority && task.priority !== filters.priority) return false;
        if (filters.assignee && task.assignedTo?.id !== filters.assignee) return false;
        return true;
      })
      .filter(task => task.expectedCompletionDate) // Only show tasks with due dates
      .map(task => {
        const dueDate = new Date(task.expectedCompletionDate!);
        const startDate = task.createdAt ? new Date(task.createdAt) : dueDate;
        
        return {
          id: task._id!,
          title: `#${task.taskNumber} ${task.title}`,
          start: startDate,
          end: dueDate,
          resource: task
        };
      });
  }, [tasks, filters]);

  const eventStyleGetter = (event: CalendarEvent) => {
    const task = event.resource;
    const color = priorityColors[task.priority];
    
    return {
      style: {
        backgroundColor: color.bg,
        borderLeft: `4px solid ${color.border}`,
        color: color.text,
        fontSize: '0.875rem',
        padding: '4px 8px',
        borderRadius: '4px',
        cursor: 'pointer'
      }
    };
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    onTaskClick(event.id);
  };

  const handleNavigate = (newDate: Date) => {
    setDate(newDate);
  };

  const handleViewChange = (newView: View) => {
    setView(newView);
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '', assignee: '' });
  };

  const hasActiveFilters = filters.status || filters.priority || filters.assignee;

  // Get unique assignees
  const assignees = useMemo(() => {
    const uniqueAssignees = new Map();
    tasks.forEach(task => {
      if (task.assignedTo) {
        uniqueAssignees.set(task.assignedTo.id, task.assignedTo);
      }
    });
    return Array.from(uniqueAssignees.values());
  }, [tasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-blue-600" />
            Task Calendar
          </h1>
          <p className="text-gray-600 mt-1">View tasks by due date on calendar</p>
        </div>
        <button
          onClick={fetchTasks}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tasks</p>
              <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
            </div>
            <CalendarIcon className="w-8 h-8 text-blue-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With Due Dates</p>
              <p className="text-2xl font-bold text-gray-900">{events.length}</p>
            </div>
            <Clock className="w-8 h-8 text-green-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overdue</p>
              <p className="text-2xl font-bold text-gray-900">
                {events.filter(e => e.end < new Date() && e.resource.status !== 'completed').length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-orange-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Due This Week</p>
              <p className="text-2xl font-bold text-gray-900">
                {events.filter(e => {
                  const weekFromNow = new Date();
                  weekFromNow.setDate(weekFromNow.getDate() + 7);
                  return e.end >= new Date() && e.end <= weekFromNow;
                }).length}
              </p>
            </div>
            <CalendarIcon className="w-8 h-8 text-purple-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={20} className="text-gray-600" />
          <h2 className="font-semibold text-gray-900">Filters</h2>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            >
              <X size={16} />
              Clear Filters
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="assigned">Assigned</option>
              <option value="in-progress">In Progress</option>
              <option value="testing">Testing</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <select
              value={filters.assignee}
              onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Assignees</option>
              {assignees.map(assignee => (
                <option key={assignee.id} value={assignee.id}>{assignee.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Priority Legend</h3>
        <div className="flex flex-wrap gap-4">
          {Object.entries(priorityColors).map(([priority, color]) => (
            <div key={priority} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{
                  backgroundColor: color.bg,
                  border: `2px solid ${color.border}`
                }}
              />
              <span className="text-sm text-gray-700 capitalize">{priority}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow p-4" style={{ height: '700px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          view={view}
          onView={handleViewChange}
          date={date}
          onNavigate={handleNavigate}
          popup
          selectable
          views={['month', 'week', 'day', 'agenda']}
          messages={{
            next: 'Next',
            previous: 'Previous',
            today: 'Today',
            month: 'Month',
            week: 'Week',
            day: 'Day',
            agenda: 'Agenda',
            date: 'Date',
            time: 'Time',
            event: 'Task',
            noEventsInRange: 'No tasks scheduled in this date range.',
            showMore: (total: number) => `+${total} more`
          }}
        />
      </div>
    </div>
  );
};

export default CalendarView;
